<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'

import NavigateBack from '@/components/shared/NavigateBack.vue'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  row: {
    type: Object,
    required: true,
  },
  fields: {
    type: Array,
    required: true,
  },
  summary: {
    type: Array,
    required: false,
    default: () => [],
  },
  apply: {
    type: Function,
    required: false,
    default: (newData, field, value) => { newData[field] = value },
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { invoiceLineFieldTypeEnum } = useAccountingStore()

const line = ref({ ...props.row })
const changes = ref({})
const hasChanges = computed(() => Object.keys(changes.value).length > 0)

const onCancel = () => emit('cancel')
useNavigator(onCancel)
useEscapeClose(hasChanges, onCancel)

const isTextField = (field) => field.type === invoiceLineFieldTypeEnum.TEXT
const textFields = computed(() => props.fields.filter(isTextField))
const numberFields = computed(() => props.fields.filter(field => !isTextField(field)))
const columnClass = (count) => (isMobile.value ? 'col-12' : `col-${12 / count}`)

const onFieldChange = (field, value) => {
  const newData = {}
  props.apply(newData, field.field, isTextField(field) ? value : Number(value) || 0, line.value)
  Object.assign(line.value, newData)
  Object.assign(changes.value, newData)
}
</script>

<template>
  <q-card class="dialog-card-flex">
    <navigate-back :text="title" @cancel="onCancel" />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll dialog-card-content">
      <q-card-section class="q-pt-none">
        <q-item-label header class="row items-center q-px-none q-pb-xs">
          <q-icon
            name="mdi-file-document-outline"
            class="q-mr-sm"
            color="primary"
            size="sm"
          />
          <span class="dialog-card-section-title">{{ i18n.t('common.basicInfo') }}</span>
        </q-item-label>
        <div class="q-ml-sm row q-col-gutter-sm">
          <q-input
            v-for="item in summary"
            :key="item.label"
            :model-value="item.value"
            :class="columnClass(summary.length)"
            :label="item.label"
            readonly
          />
          <q-input
            v-for="field in textFields"
            :key="field.field"
            :model-value="line[field.field]"
            class="col-12"
            :label="field.label"
            autogrow
            @update:model-value="value => onFieldChange(field, value)"
          />
          <q-input
            v-for="field in numberFields"
            :key="field.field"
            :model-value="line[field.field]"
            :class="columnClass(numberFields.length)"
            type="number"
            min="0"
            :label="field.label"
            :suffix="field.type === invoiceLineFieldTypeEnum.PERCENT ? '%' : undefined"
            @update:model-value="value => onFieldChange(field, value)"
          />
        </div>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="hasChanges" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        @click="emit('update', changes)"
      />
    </q-card-actions>
  </q-card>
</template>
